/**
 * Utilitários para cálculo e formatação de duração de procedimentos.
 */

/**
 * Identifica se um procedimento/serviço corresponde a Buço (ex: "Buço", "Depilação de Buço", "Buço na linha", etc.)
 */
export function isBucoService(servicoOrName) {
  const nome = typeof servicoOrName === 'string'
    ? servicoOrName
    : (servicoOrName?.nome || servicoOrName?.servico_nome || '');

  if (!nome) return false;

  const normalized = String(nome)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  return /\bbuco\b/i.test(normalized);
}

/**
 * Calcula a duração estimada de uma lista de serviços selecionados para agendamento.
 * Regra do ERP da Fran:
 * Quando o serviço Buço é selecionado com mais algum outro serviço, o tempo de atendimento do buço (30 min)
 * é desconsiderado, pois é realizado enquanto se espera algum produto agir no outro procedimento.
 * Caso apenas Buço seja selecionado, a duração normal (30 min) é mantida.
 * 
 * @param {Array} servicos - Lista de objetos de serviços selecionados
 * @returns {{ totalMinutos: number, bucoDesconsiderado: boolean }}
 */
export function calculateEstimatedDuration(servicos = []) {
  if (!Array.isArray(servicos) || servicos.length === 0) {
    return { totalMinutos: 0, bucoDesconsiderado: false };
  }

  const hasBuco = servicos.some((s) => isBucoService(s));
  const hasOutroServico = servicos.some((s) => !isBucoService(s));
  const bucoDesconsiderado = hasBuco && hasOutroServico;

  const totalMinutos = servicos.reduce((total, s) => {
    if (bucoDesconsiderado && isBucoService(s)) {
      return total;
    }

    const duracao = Number(s?.duracao_minutos);
    if (Number.isFinite(duracao) && duracao > 0) {
      return total + duracao;
    }

    return total + (isBucoService(s) ? 30 : 60);
  }, 0);

  return { totalMinutos, bucoDesconsiderado };
}

/**
 * Formata minutos em texto legível (ex: "45 min", "1h", "1h 30min", "2h").
 */
export function formatDurationText(total) {
  const val = Number(total) || 0;
  if (val <= 0) return '0 min';
  if (val >= 60 && val % 60 === 0) return `${val / 60}h`;
  if (val > 60) return `${Math.floor(val / 60)}h ${val % 60}min`;
  return `${val} min`;
}
